import { useCallback, useLayoutEffect, useRef, useState } from "react"
import Cropper from "react-easy-crop"
import "react-easy-crop/react-easy-crop.css"
import { Button, Icon, Modal } from "../../../components"
import getCroppedImg from "../../../utils/cropImage"
import "./modal-signature-crop.scss"

// Tamanho exato do espaço de assinatura (600x142). A moldura de recorte usa
// esse mesmo tamanho em px na tela (1:1), para o usuário ver exatamente como a
// assinatura vai ficar. Renderizamos o PNG final em 2x para nitidez no PDF,
// mantendo a mesma proporção.
export const OUTPUT_WIDTH = 600
export const OUTPUT_HEIGHT = 142
const OUTPUT_SCALE = 2

const CROP_ASPECT = OUTPUT_WIDTH / OUTPUT_HEIGHT

const ModalSignatureCrop = ({ show, imageUrl, onClose, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [cropSize, setCropSize] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const cropAreaRef = useRef(null)

  // A moldura é exatamente 600x142 quando cabe (desktop). Em telas mais estreitas
  // encolhe proporcionalmente — sem scroll e sem cortar. Só o react-easy-crop
  // sabe a largura real do container, por isso medimos aqui.
  useLayoutEffect(() => {
    const el = cropAreaRef.current
    if (!el) {
      return undefined
    }

    const updateCropSize = () => {
      const available = el.clientWidth
      if (!available) {
        return
      }

      const width = Math.min(OUTPUT_WIDTH, available)
      setCropSize({ width: Math.round(width), height: Math.round(width / CROP_ASPECT) })
    }

    updateCropSize()

    const observer = new ResizeObserver(updateCropSize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const handleCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const rotateLeft = () => setRotation((prev) => (prev - 90 + 360) % 360)
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360)

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { blob, url } = await getCroppedImg(
        imageUrl,
        croppedAreaPixels,
        rotation,
        OUTPUT_WIDTH * OUTPUT_SCALE,
        OUTPUT_HEIGHT * OUTPUT_SCALE,
      )
      onConfirm(blob, url)
    } catch (err) {
      setError("Não foi possível recortar a imagem. Tente novamente.")
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="modal-signature-crop">
      <Modal.Header>
        <Button variant="icon" className="btn-close" onClick={onClose}>
          <Icon id="icon-close" size="24" />
        </Button>
        <Modal.Title as="div" className="h2">
          Ajustar assinatura
        </Modal.Title>
        <div>Posicione, aproxime e gire a imagem para encaixá-la na área.</div>
      </Modal.Header>

      <Modal.Body>
        <div className="signature-crop-area" ref={cropAreaRef}>
          {imageUrl && cropSize && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={CROP_ASPECT}
              cropSize={cropSize}
              objectFit="auto-cover"
              minZoom={1}
              maxZoom={3}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={handleCropComplete}
            />
          )}
        </div>

        <div className="signature-crop-controls">
          <div className="signature-crop-rotate">
            <Button variant="icon" onClick={rotateLeft} aria-label="Girar 90° à esquerda">
              <Icon id="icon-rotate" size="24" className="signature-crop-rotate-left" />
            </Button>
            <Button variant="icon" onClick={rotateRight} aria-label="Girar 90° à direita">
              <Icon id="icon-rotate" size="24" />
            </Button>
          </div>

          <div className="signature-crop-zoom">
            <Icon id="icon-zoom_out" size="20" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
              aria-label="Zoom"
            />
            <Icon id="icon-zoom_in" size="20" />
          </div>
        </div>

        {error && <div className="signature-crop-error">{error}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          CANCELAR
        </Button>
        <Button variant="primary" onClick={handleConfirm} loading={loading} disabled={!croppedAreaPixels}>
          <Icon id="icon-initial" className="me-1" size="16" />
          APLICAR
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalSignatureCrop
